import * as utilities from "./utilites";
import * as authToken from "./authToken.js";
import * as storageApi from "./marketdataStorage.js";
import * as symbolAuthority from "./symbolAuthority.js";
import * as workerLifecycle from "./quoteWorkerLifecycle.js";
import * as connection from "./marketdataConnection.js";
import * as subscriptions from "./marketdataSubscriptions.js";
import * as history from "./marketdataHistory.js";
import { readEvictedFlag } from "./marketdataConnection.js";
import { settings, parsedMarketdataUrls } from "./settings";
import { parseTrades, roundValue2 } from "./marketdataUtils";
import { QuoteSubscriptionManager } from "./quoteSubscriptionManager";
import { BookByPriceSubscriptionManager } from "./bookByPriceSubscriptionManager";
import logger from "../../utils/logger";
import { urlManager } from "../../utils/urlManager";
import { PRODUCT_NAME, PRODUCT_VERSION } from "../../constants/product";

// Symbol Filter types from MarketData API
// H = Master futures (WINFUT, WDOFUT, etc.)
// F = Futures series (WING26, WINZ25, etc.)

// #304: per-tab sticky eviction. sessionStorage survives F5 but a brand-new tab
// gets a clean slate, which matches the "this window is dead" semantics.

const TOKEN_COMMAND = "/api/security/token";

export class Marketdata {
  constructor() {
    this.signalRToken = "";
    this.broker = "TZ";
    this.user = "";
    this.auth = "";
    this.receivers = [];
    this.signalRConnection = {};
    this.symbols = new Map();
    this.objectSymbolTable = new Map();
    this.state = "offline";
    // #304: eviction is sticky across page reloads (sessionStorage) — F5 must not reconnect.
    // The flag is cleared only when the tab is closed or by explicit clearEvictedFlag().
    this.closedByServer = readEvictedFlag();
    this.lastQuotes = new Map(); // canonical → synthesized quote (replaces lastPrices, lastSnapshots, lastQuoteTimePerSymbol)
    this.quoteStalenessMonitorId = null; // Periodic staleness checker interval
    this.registeredSymbols = new Map();
    this.refreshTokenIntervalId = 0;
    this.lastParsedSymbol = "";
    this.lastParsedDate = "";
    this.lastParsedTime = "";
    this.lastParsedPrice = 0;
    this.lastHeartbeat = Date.now();
    this.symbolsTimestamp = 0; // epoch ms of the currently-loaded symbols set (gate for FUT/FUL trading)
    this._symbolsRefreshInFlight = false; // dedupe: one symbols refresh at a time
    this._lastRefreshAttempt = 0; // backoff: don't retry a failing refresh more than 1x/60s
    this.connectionId = 0; // Incremental connection ID for tracing
    this.reconnectionAttempts = 0;
    this.slowConsumerReconnects = 0; // count of slow-consumer drops (observability)
    this.maxReconnectionAttempts =
      parseInt(import.meta.env.VITE_SIGNALR_MAX_RECONNECTION_ATTEMPTS) || 10;
    this.baseReconnectionDelay =
      parseInt(import.meta.env.VITE_SIGNALR_BASE_RECONNECTION_DELAY) || 10000; // 10 seconds
    this.maxReconnectionDelay =
      parseInt(import.meta.env.VITE_SIGNALR_MAX_RECONNECTION_DELAY) || 300000; // 5 minutes
    this.isRefreshingToken = false; // Race condition protection for proactive token refresh
    this.consecutiveAuthFailures = 0; // Counter for consecutive auth failures (only log after 5)
    this.consecutiveConnectionErrors = 0; // Counter for consecutive connection errors (only log after 3)
    this.quoteWorker = null; // Web Worker for quote coalescing (offloads processing from main thread)
    // Raw (pre-coalescing) per-trade quote taps for the history↔RT seam reconcile.
    // Each entry: { symbol, cb }. Empty in steady state → zero hot-path overhead.
    this.rawQuoteTaps = [];
    this.adaptiveThrottleInterval = null; // Interval for FPS-based adaptive throttling
    this.currentQuoteThrottle = 250; // Current throttle interval (exposed for debug overlay)
    this.subscriptionManager = new QuoteSubscriptionManager({
      onFirstSubscriber: (canonical) => {
        this.registeredSymbols.set(canonical, null);
        // Reset worker's daily values cache so D* fields are sent in full on next tick
        if (this.quoteWorker) {
          this.quoteWorker.postMessage({
            type: "RESET_DAILY_CACHE",
            symbol: canonical,
          });
        }
        if (this.signalRConnection && this.signalRConnection.send) {
          this.signalRConnection.send("register", "q", canonical);
          console.log(
            `[Marketdata] 📡 ${canonical} registered (first subscriber)`,
          );
          logger.diagnostic.info(`Symbol registered: ${canonical}`, {
            symbol: canonical,
            event: "register",
          });
        }
      },
      onLastUnsubscriber: (canonical) => {
        if (this.signalRConnection && this.signalRConnection.send) {
          this.signalRConnection.send("unregister", "q", canonical);
          console.log(
            `[Marketdata] 🔌 ${canonical} unregistered (last subscriber)`,
          );
          logger.diagnostic.info(`Symbol unregistered: ${canonical}`, {
            symbol: canonical,
            event: "unregister",
          });
        }
        this.registeredSymbols.delete(canonical);
      },
      getCanonicalSymbol: (s) => this.getCanonicalSymbol(s),
    });
    this.symbolSubscribers = this.subscriptionManager.symbolSubscribers; // backward compat for any direct Map access

    // BookByPrice (BBP) subscription manager — ref-counted per-symbol upstream registration.
    // Subscriber ID convention: `book-${panelId}` (mirrors Chart pattern `chart-${id}`).
    // Uses the same canonical resolver as quotes so WIN/WDO family aliases share a single
    // upstream subscription. When `getConsolidatableFamily` is expanded to other families
    // in the future, BBP benefits automatically.
    this.bookByPriceSubscriptionManager = new BookByPriceSubscriptionManager({
      onFirstSubscriber: (canonical) => {
        this.sendBookByPriceRegister(canonical);
      },
      onLastUnsubscriber: (canonical) => {
        this.sendBookByPriceUnregister(canonical);
      },
      getCanonicalSymbol: (s) => this.getCanonicalSymbol(s),
    });
  }

  getUrl = () => {
    const urls = parsedMarketdataUrls[this.broker] || [];
    if (urls.length === 0) {
      // This should not happen if runtime-config is properly loaded
      console.error(
        "[Marketdata] parsedMarketdataUrls is empty - config may not be loaded yet!",
      );
      // Fallback to original settings (but this may use production URLs)
      return settings.marketdataUrl[this.broker];
    }
    return urlManager.getCurrentUrl(this.broker, "marketdata", urls);
  };

  setUrl = (url) => {
    settings.marketdataUrl[this.broker] = url;
  };

  // ============================================
  // WIN/WDO Symbol Family Consolidation Methods
  // ============================================
  //
  // Only consolidates symbols that represent the SAME underlying:
  // - WINFUT (master symbol)
  // - WINFUL (full lot version)
  // - Current contract from objectSymbolTable (e.g., WING26)
  //
  // Does NOT consolidate:
  // - Next contracts (e.g., WINI26) - different expiration
  // - Expired contracts (e.g., WINZ25) - historical only
  // ============================================

  // ── Symbol family helpers (delegado p/ symbolAuthority.js — bloco I1) ─────
  getConsolidatableFamily = (symbol) =>
    symbolAuthority.getConsolidatableFamily(this, symbol);
  getCanonicalSymbol = (symbol) =>
    symbolAuthority.getCanonicalSymbol(this, symbol);
  isSameFamily = (symbol1, symbol2) =>
    symbolAuthority.isSameFamily(this, symbol1, symbol2);
  getRelatedSymbols = (symbol) =>
    symbolAuthority.getRelatedSymbols(this, symbol);

  /**
   * Get the last known quote for a symbol, resolving to canonical if needed.
   * @param {string} symbol - Any symbol variant (e.g., "WING26", "WINFUT", "PETR4")
   * @returns {object|undefined} - Synthesized quote object or undefined
   */
  getLastQuote = (symbol) => {
    const canonical = this.getCanonicalSymbol(symbol);
    return this.lastQuotes.get(canonical);
  };

  /**
   * Update the synthesized last quote for a symbol.
   * Stores on canonical key, mutates in-place (zero alloc on hot path).
   * @param {object} tick - Quote tick with S, C, O, H, L, D*, ON, PON, etc.
   */
  updateLastQuote = (tick) => {
    const canonical = this.getCanonicalSymbol(tick.S);
    let entry = this.lastQuotes.get(canonical);
    if (!entry) {
      entry = { S: canonical };
      this.lastQuotes.set(canonical, entry);
    }
    if (tick.C != null) entry.C = tick.C;
    if (tick.O != null) entry.O = tick.O;
    if (tick.H != null) entry.H = tick.H;
    if (tick.L != null) entry.L = tick.L;
    if (tick.Q != null) entry.Q = tick.Q;
    if (tick.DO != null) entry.DO = tick.DO;
    if (tick.DH != null) entry.DH = tick.DH;
    if (tick.DL != null) entry.DL = tick.DL;
    if (tick.DV != null) entry.DV = tick.DV;
    if (tick.DF != null) entry.DF = tick.DF;
    if (tick.PC != null) entry.PC = tick.PC;
    if (tick.VW != null) entry.VW = tick.VW;
    if (tick.ON != null) entry.ON = tick.ON;
    if (tick.PON != null) entry.PON = tick.PON;
    if (tick.DT != null) entry.DT = tick.DT;
    if (tick.TY != null) entry.TY = tick.TY;
    entry._receivedAt = Date.now();
  };

  /**
   * Broadcast quote to all registered receivers
   */
  broadcastQuoteToReceivers = (quote) => {
    if (this.receivers.length > 0) {
      const r = this.receivers.slice(0);
      r.forEach((receiver) => {
        if (receiver.OnQuote !== undefined) receiver.OnQuote(quote);
      });
    }
  };

  refreshToken = async () => {
    const tokenUrl = `${this.getUrl()}${TOKEN_COMMAND}`;
    const tokenRequest = {
      Broker: this.broker,
      Auth: this.auth,
      Product: PRODUCT_NAME,
      Version: PRODUCT_VERSION,
    };

    try {
      const token = await utilities.postJsonToURL(
        tokenUrl,
        tokenRequest,
        undefined,
      );
      const r = JSON.parse(token);
      this.signalRToken = r.Token;
      this.user = r.User;
      console.log(
        `[${new Date().toISOString()}] ✅ Token refreshed for ${this.broker} (${PRODUCT_NAME} v${PRODUCT_VERSION})`,
      );

      // Register auth provider for error reporting (lazy import to avoid circular deps)
      import("../../utils/errorReportService").then(
        ({ default: errorReporter }) => {
          errorReporter.setAuthProvider(() => ({
            token: this.signalRToken,
            broker: this.broker,
            user: this.user,
          }));
        },
      );

      // Remote log shipping — honour the server whitelist flag on the token
      // response. Flag changes propagate on every refresh (every 30min), so
      // admins toggling mid-session take effect at most one refresh later
      // (or immediately via the batch endpoint's 403 path).
      import("../../utils/logBatcher").then(({ default: logBatcher }) => {
        logBatcher.setAuthProvider(() => ({
          token: this.signalRToken,
          broker: this.broker,
          user: this.user,
        }));
      });
      import("../../utils/logger").then(({ setRemoteShipping }) => {
        setRemoteShipping(!!r.RemoteLogging);
      });

      // Mark server as healthy after successful token refresh
      const urls = parsedMarketdataUrls[this.broker] || [];
      if (urls.length > 1) {
        urlManager.markSuccess(this.broker, "marketdata");
      }
    } catch (error) {
      console.error(
        `[${new Date().toISOString()}] ❌ Token refresh failed for ${this.broker}:`,
        error.message,
      );

      // Mark server as unhealthy - this allows failover in startUsingAuth retry loop
      const urls = parsedMarketdataUrls[this.broker] || [];
      if (urls.length > 1) {
        urlManager.markFailureAndGetNext(
          this.broker,
          "marketdata",
          urls,
          error,
        );
      }

      throw error; // Re-throw to allow load balancing logic to catch it
    }
  };

  // ── JWT helpers (delegado) ────────────────────────────────────────────────
  // Puras, unificadas em authToken.js (a cópia daqui e a do outro serviço
  // eram idênticas — equivalência pinada em authTokenContract.test.js).
  parseTokenExpiration = (token) => authToken.parseTokenExpiration(token);
  isTokenExpired = (token, bufferMinutes = 5) =>
    authToken.isTokenExpired(token, bufferMinutes);

  getValidToken = async () => {
    // If no token exists, we need a new one
    if (!this.signalRToken) {
      console.log(
        `[${new Date().toISOString()}] 🔑 No marketdata token available, refreshing...`,
      );
      await this.refreshToken();
      return this.signalRToken;
    }

    // Check if current token is expired
    if (this.isTokenExpired(this.signalRToken)) {
      const expirationDate = this.parseTokenExpiration(this.signalRToken);
      console.log(
        `[${new Date().toISOString()}] 🔑 Marketdata token expired (${expirationDate?.toISOString()}), refreshing...`,
      );
      await this.refreshToken();

      // Log new token expiration
      const newExpirationDate = this.parseTokenExpiration(this.signalRToken);
      console.log(
        `[${new Date().toISOString()}] ✅ Marketdata token refreshed, new expiration: ${newExpirationDate?.toISOString()}`,
      );

      return this.signalRToken;
    }

    // Token is still valid
    return this.signalRToken;
  };

  parseTrades = (msg) =>
    parseTrades(
      msg,
      this.lastParsedPrice,
      this.lastParsedSymbol,
      this.lastParsedDate,
      this.lastParsedTime,
    );

  /**
   * Main-thread ingress for a raw per-trade quote arriving on the SignalR "Q"
   * event. Extracted from the .on("Q", …) closure so tests can drive the REAL
   * worker + seam-tap path without a socket (window.__MARKETDATA__._onQuoteFromSocket).
   * Behavior is identical to the inline handler it replaced.
   * @param {Object} quote - raw per-trade quote (already decoded by SignalR)
   */
  _onQuoteFromSocket = (quote) => {
    this.lastHeartbeat = Date.now();

    // Deliver the RAW per-trade quote to any seam-reconcile taps BEFORE the
    // worker coalesces it. Taps only exist while a chart is loading history,
    // so this loop is empty (and free) in steady state. (history↔RT seam fix)
    if (this.rawQuoteTaps.length > 0) {
      this.dispatchRawQuoteToTaps(quote);
    }

    // Forward raw quote to worker for processing
    // Worker will postMessage back coalesced quotes at controlled rate
    if (this.quoteWorker) {
      this.quoteWorker.postMessage(quote);
    } else {
      // Fallback: process on main thread if worker unavailable
      // This maintains functionality but without performance benefit
      if (quote.O) quote.O = roundValue2(quote.O);
      if (quote.H) quote.H = roundValue2(quote.H);
      if (quote.L) quote.L = roundValue2(quote.L);
      if (quote.C) quote.C = roundValue2(quote.C);
      this.updateLastQuote(quote);
      this.broadcastQuoteToReceivers(quote);
    }
  };

  /**
   * Inject a quote into the quote processing pipeline
   * Used by replay mode to simulate real-time quotes
   * @param {Object} quote - Quote object to inject
   */
  injectReplayQuote = (quote) => {
    // Safety check: only allow in debug mode
    if (import.meta.env.VITE_DEBUG_REPLAY_MODE !== "true") {
      console.error("[Marketdata] injectReplayQuote: Debug mode not enabled");
      return;
    }

    // Mark as replay quote
    if (!quote._isReplay) {
      console.warn(
        "[Marketdata] injectReplayQuote: Quote not marked as replay",
      );
      quote._isReplay = true;
    }

    // Round values (same as SignalR handler)
    if (quote.O) quote.O = roundValue2(quote.O);
    if (quote.H) quote.H = roundValue2(quote.H);
    if (quote.L) quote.L = roundValue2(quote.L);
    if (quote.C) quote.C = roundValue2(quote.C);

    // Update synthesized last quote
    this.updateLastQuote(quote);

    // Notify all receivers (same as SignalR "Q" event)
    if (this.receivers.length > 0) {
      const r = this.receivers.slice(0);
      r.forEach((receiver) => {
        if (receiver.OnQuote !== undefined) {
          receiver.OnQuote(quote);
        }
      });
    }
  };

  // ── Connection runbook (delegado p/ marketdataConnection.js — 🔴) ─────────
  // runConnection/startUsing* — state machine de sessão SignalR com failover.
  // Contrato: marketdataConnectionPolicyContract.test.js + e2e (todo login).
  runConnection = () => connection.runConnection(this);
  startUsingCredentials = (broker, user, pwd) =>
    connection.startUsingCredentials(this, broker, user, pwd);
  startUsingAuth = (broker, auth) =>
    connection.startUsingAuth(this, broker, auth);

  initQuoteWorker = () => workerLifecycle.initQuoteWorker(this);
  terminateQuoteWorker = () => workerLifecycle.terminateQuoteWorker(this);
  startAdaptiveThrottling = () => workerLifecycle.startAdaptiveThrottling(this);
  stopAdaptiveThrottling = () => workerLifecycle.stopAdaptiveThrottling(this);
  startQuoteStalenessMonitor = () =>
    workerLifecycle.startQuoteStalenessMonitor(this);
  stopQuoteStalenessMonitor = () =>
    workerLifecycle.stopQuoteStalenessMonitor(this);
  debugWorkerState = () => workerLifecycle.debugWorkerState(this);

  // ── Stop/reconexão (delegado p/ marketdataConnection.js — 🔴) ─────────────
  stop = () => connection.stop(this);
  getReconnectionDelay = () => connection.getReconnectionDelay(this);
  _handleDisconnectedFromServer = (from, message, reason) =>
    connection._handleDisconnectedFromServer(this, from, message, reason);
  _handleSocketClose = (conn, error) =>
    connection._handleSocketClose(this, conn, error);
  resetReconnectionAttempts = () => connection.resetReconnectionAttempts(this);
  isRecoverableError = (error) => connection.isRecoverableError(this, error);
  checkConnectivity = () => connection.checkConnectivity(this);
  reloadAllChartsData = () => connection.reloadAllChartsData(this);
  reregisterAllSymbols = () => connection.reregisterAllSymbols(this);

  // ── Subscriptions/ingress (delegado p/ marketdataSubscriptions.js) ────────
  // Taps do seam, ref-counting de quotes, transporte BBP, reset do worker.
  // Contrato: marketdataIngressHistoryContract.test.js + quote-ref e2e.
  registerRawTap = (symbol, cb) =>
    subscriptions.registerRawTap(this, symbol, cb);
  dispatchRawQuoteToTaps = (quote) =>
    subscriptions.dispatchRawQuoteToTaps(this, quote);
  registerQuote = (symbol, subscriberId = "legacy") =>
    subscriptions.registerQuote(this, symbol, subscriberId);
  unregisterQuote = (symbol, subscriberId = "legacy") =>
    subscriptions.unregisterQuote(this, symbol, subscriberId);
  registerBookByPrice = (symbol, subscriberId) =>
    subscriptions.registerBookByPrice(this, symbol, subscriberId);
  unregisterBookByPrice = (symbol, subscriberId) =>
    subscriptions.unregisterBookByPrice(this, symbol, subscriberId);
  hasBookByPriceSubscribers = (symbol) =>
    subscriptions.hasBookByPriceSubscribers(this, symbol);
  debugBookByPriceSubscriptions = () =>
    subscriptions.debugBookByPriceSubscriptions(this);
  sendBookByPriceRegister = (symbol) =>
    subscriptions.sendBookByPriceRegister(this, symbol);
  sendBookByPriceUnregister = (symbol) =>
    subscriptions.sendBookByPriceUnregister(this, symbol);
  broadcastBookByPriceRawToReceivers = (book) =>
    subscriptions.broadcastBookByPriceRawToReceivers(this, book);
  reregisterAllBookByPriceSymbols = () =>
    subscriptions.reregisterAllBookByPriceSymbols(this);
  resetWorkerDailyCache = (symbols) =>
    subscriptions.resetWorkerDailyCache(this, symbols);
  hasQuoteSubscribers = (symbol) =>
    subscriptions.hasQuoteSubscribers(this, symbol);
  debugQuoteSubscriptions = () => subscriptions.debugQuoteSubscriptions(this);

  // ── Symbol authority (delegado p/ symbolAuthority.js — bloco I1 🔴) ───────
  // Resolução de símbolo, gate 08:55, metadados e ciclo de vida do set de
  // símbolos. Contratos: symbolAuthorityContract.test.js +
  // orderConversionsGateSeam.integration.test.js. Estado segue na instância.
  getObjectSymbolTable = () => symbolAuthority.getObjectSymbolTable(this);
  _gate0855Today = () => symbolAuthority._gate0855Today(this);
  symbolsFreshForTrading = () => symbolAuthority.symbolsFreshForTrading(this);
  _dispatchSymbolsRefresh = () => symbolAuthority._dispatchSymbolsRefresh(this);
  ensureSymbolsFreshForTrading = () =>
    symbolAuthority.ensureSymbolsFreshForTrading(this);
  _maybeRefreshSymbolsOnHeartbeat = () =>
    symbolAuthority._maybeRefreshSymbolsOnHeartbeat(this);
  resolveSymbol = (symbol) => symbolAuthority.resolveSymbol(this, symbol);
  resolveSymbolForTrading = (symbol) =>
    symbolAuthority.resolveSymbolForTrading(this, symbol);
  getSymbol = (code) => symbolAuthority.getSymbol(this, code);
  getFOQ = (code) => symbolAuthority.getFOQ(this, code);
  getStandardLot = (code) => symbolAuthority.getStandardLot(this, code);
  getMinInc = (code) => symbolAuthority.getMinInc(this, code);
  getMarketOpenHour = (symbol) =>
    symbolAuthority.getMarketOpenHour(this, symbol);
  getSymbols = (cb, options = {}) =>
    symbolAuthority.getSymbols(this, cb, options);
  refreshSymbolsInBackground = () =>
    symbolAuthority.refreshSymbolsInBackground(this);

  // ── History fetch (delegado p/ marketdataHistory.js — bloco 4) ────────────
  // getBars OdinMD V3 + abort por chartId (#358). Estado do Map na instância.
  _barsAbortControllers = new Map();
  _releaseBarsController = (chartId, controller) =>
    history._releaseBarsController(this, chartId, controller);
  getBars = (symbol, suggestedStartDate, suggestedEndDate, params) =>
    history.getBars(this, symbol, suggestedStartDate, suggestedEndDate, params);

  // ── Storage API (delegado) ─────────────────────────────────────────────────
  // CRUD de /api/storage + /api/specs extraído para marketdataStorage.js
  // (modularização god-object). Assinaturas/retornos idênticos; contrato
  // pinado em __tests__/marketdataStorageContract.test.js.
  saveDefaults = (content) => storageApi.saveDefaults(this, content);
  getDefaults = () => storageApi.getDefaults(this);
  saveSettings = (content) => storageApi.saveSettings(this, content);
  getSettings = () => storageApi.getSettings(this);
  saveDesktop = (id, _name, _content) =>
    storageApi.saveDesktop(this, id, _name, _content);
  getDesktopList = () => storageApi.getDesktopList(this);
  getDesktop = (id) => storageApi.getDesktop(this, id);
  deleteDesktop = (id) => storageApi.deleteDesktop(this, id);
  saveTemplate = (id, _name, _content) =>
    storageApi.saveTemplate(this, id, _name, _content);
  getTemplateList = () => storageApi.getTemplateList(this);
  getTemplate = (id) => storageApi.getTemplate(this, id);
  deleteTemplate = (id) => storageApi.deleteTemplate(this, id);
  getTemplates = () => storageApi.getTemplates(this);
  saveTemplates = (_content) => storageApi.saveTemplates(this, _content);
  deleteTemplates = () => storageApi.deleteTemplates(this);
  saveRating = (ratingData) => storageApi.saveRating(this, ratingData);
  getRating = () => storageApi.getRating(this);
  savePoll = (pollData) => storageApi.savePoll(this, pollData);
  getPoll = () => storageApi.getPoll(this);
  saveSpecs = (report) => storageApi.saveSpecs(this, report);

  registerReceiver = (receiver) => {
    if (!receiver || typeof receiver !== "object") return;
    if (!this.receivers.includes(receiver)) {
      this.receivers.push(receiver);
    }
  };

  unregisterReceiver = (receiver) => {
    this.receivers = this.receivers.filter((r) => r !== receiver);
  };
}

const marketdata = new Marketdata();
export default marketdata;

// Expose on window for error reporting and E2E test instrumentation
// (matches the pattern used by chartStore.js / compareStore.js).
if (typeof window !== "undefined") {
  window.__MARKETDATA__ = marketdata;
}
